/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Presentational building blocks shared by the chatroom profile popover
// (`ProfileCard`, Figma 4139:10345) and the full profile modal (`ProfileModal`,
// Figma 4136:14975). Tailwind-first: every static value is a className (design
// tokens where one exists, arbitrary-value classes for exact Figma values).
// The only inline styles are the avatar's runtime pixel size (a prop a static
// class can't express) and the lucide icon `color` (needs a literal, not a
// class).

import React, { useEffect, useState } from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import { ChevronRight, MoreHorizontal, UserPlus } from 'lucide-react-native';

import { Skeleton } from '../components/Skeleton';
import { VerifiedBadge } from '../components/VerifiedBadge';
import type { ProfileActions, ProfileStatusView, ProfileView } from './profileViewTypes';

/** Muted glyph tint for the secondary action icons + chevron. */
const ICON_MUTED = '#8E8E93';

function initialsOf(name: string): string {
  const initials = name
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
  // Never render an empty squircle — fall back to a neutral glyph when the name
  // is blank (some directory rows resolve to an empty displayName).
  return initials.length > 0 ? initials : '?';
}

/**
 * Squircle avatar at an exact pixel size. `sizeClassName` carries the
 * width/height/radius (e.g. "w-[80px] h-[80px] rounded-[12px]"); falls back to
 * initials on a panel surface when no image is supplied.
 */
export function ProfileAvatar({
  name,
  uri,
  sizeClassName,
}: {
  name: string;
  uri?: string | undefined;
  sizeClassName: string;
}): JSX.Element {
  // Fall back to initials when the image fails to load — a group/peer avatar is
  // an EXPIRING SigV4 presigned URL, so a cached one that lapsed (403) would
  // otherwise render as an empty box. Reset the failure flag whenever the URL
  // changes (a fresh re-resolve should get another chance to load).
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [uri]);

  const showImage = uri !== undefined && uri.length > 0 && !failed;
  return (
    <View
      className={`overflow-hidden items-center justify-center bg-light-surface-elevated dark:bg-dark-surface-elevated ${sizeClassName}`}
    >
      {showImage ? (
        <Image
          source={{ uri }}
          className="w-full h-full"
          resizeMode="cover"
          onError={() => setFailed(true)}
          accessibilityLabel=""
        />
      ) : (
        <Text className="text-[18px] font-semibold text-light-text-secondary dark:text-dark-text-secondary">
          {initialsOf(name)}
        </Text>
      )}
    </View>
  );
}

/** Name + verified badge + job title. The name is pressable when `onNamePress`
 *  is supplied (the popover uses this to escalate to the full modal). */
export function ProfileIdentity({
  name,
  jobTitle,
  verified,
  onNamePress,
}: {
  name: string;
  jobTitle?: string | undefined;
  verified?: boolean | undefined;
  onNamePress?: (() => void) | undefined;
}): JSX.Element {
  const nameRow = (
    <View className="flex-row items-center gap-1">
      <Text
        className="text-[16px] font-bold leading-[22px] text-light-text-primary dark:text-dark-text-primary"
        numberOfLines={1}
      >
        {name}
      </Text>
      {verified === true ? <VerifiedBadge verified size={16} /> : null}
    </View>
  );
  return (
    <View className="gap-1">
      {onNamePress !== undefined ? (
        <Pressable
          onPress={onNamePress}
          accessibilityRole="button"
          accessibilityLabel={`View ${name}'s full profile`}
          className="self-start active:opacity-70"
        >
          {nameRow}
        </Pressable>
      ) : (
        nameRow
      )}
      {jobTitle !== undefined && jobTitle.length > 0 ? (
        <Text
          className="text-[14px] leading-[20px] text-light-text-secondary dark:text-dark-text-secondary"
          numberOfLines={1}
        >
          {jobTitle}
        </Text>
      ) : null}
    </View>
  );
}

/** "🍕 On lunch · Until 13:23" status row. Renders nothing without text. */
export function ProfileStatusRow({ status }: { status?: ProfileStatusView | undefined }): JSX.Element | null {
  if (status === undefined || status.text.length === 0) return null;
  return (
    <View className="flex-row items-center gap-2">
      <View className="flex-row items-center gap-1">
        {status.emoji !== undefined && status.emoji.length > 0 ? (
          <Text className="text-[14px] leading-[20px]">{status.emoji}</Text>
        ) : null}
        <Text className="text-[14px] font-bold leading-[20px] text-light-text-primary dark:text-dark-text-primary">
          {status.text}
        </Text>
      </View>
      {status.until !== undefined && status.until.length > 0 ? (
        <Text className="text-[14px] leading-[20px] text-light-text-tertiary dark:text-dark-text-tertiary">
          {status.until}
        </Text>
      ) : null}
    </View>
  );
}

/** Send-message (accent) + add-contact + more action row. `moreRef` lets the
 *  host anchor a popover to the ⋯ button. */
export function ProfileActionsRow({
  actions,
  moreRef,
}: {
  actions: ProfileActions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  moreRef?: React.MutableRefObject<any> | undefined;
}): JSX.Element {
  // Each control renders ONLY when its handler is wired — an action with no
  // callback would look tappable but silently do nothing. `onSendMessage` is the
  // primary CTA; when it's absent the row falls back to whatever secondary
  // actions exist (and renders empty if none, rather than a dead accent button).
  return (
    <View className="flex-row items-center gap-2">
      {actions.onSendMessage !== undefined ? (
        <Pressable
          onPress={actions.onSendMessage}
          accessibilityRole="button"
          accessibilityLabel="Send message"
          className="h-7 flex-1 flex-row items-center justify-center rounded-md bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80"
        >
          <Text className="text-[14px] font-bold text-white">Send message</Text>
        </Pressable>
      ) : null}
      {actions.onAddContact !== undefined ? (
        <Pressable
          onPress={actions.onAddContact}
          accessibilityRole="button"
          accessibilityLabel="Add contact"
          hitSlop={6}
          className="h-7 w-7 items-center justify-center rounded-md active:opacity-60"
        >
          <UserPlus size={18} color={ICON_MUTED} />
        </Pressable>
      ) : null}
      {actions.onMore !== undefined ? (
        <Pressable
          ref={moreRef as never}
          onPress={actions.onMore}
          accessibilityRole="button"
          accessibilityLabel="More options"
          hitSlop={6}
          className="h-7 w-7 items-center justify-center rounded-md active:opacity-60"
        >
          <MoreHorizontal size={18} color={ICON_MUTED} />
        </Pressable>
      ) : null}
    </View>
  );
}

/** Avatar + "local time" row, identity, status, then the action row — the
 *  header block both surfaces share (only the avatar size differs). */
export function ProfileHeaderBlock({
  view,
  actions,
  avatarSizeClassName,
  onNamePress,
  moreRef,
}: {
  view: ProfileView;
  actions: ProfileActions;
  avatarSizeClassName: string;
  onNamePress?: (() => void) | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  moreRef?: React.MutableRefObject<any> | undefined;
}): JSX.Element {
  return (
    <View className="gap-3">
      <View className="flex-row items-start justify-between">
        <ProfileAvatar name={view.name} uri={view.avatarUri} sizeClassName={avatarSizeClassName} />
        {view.localTime !== undefined && view.localTime.length > 0 ? (
          <Text
            className="text-[14px] leading-[20px] text-light-text-tertiary dark:text-dark-text-tertiary"
            numberOfLines={1}
          >
            {view.localTime} Local time
          </Text>
        ) : null}
      </View>
      <ProfileIdentity
        name={view.name}
        jobTitle={view.jobTitle}
        verified={view.verified}
        onNamePress={onNamePress}
      />
      <ProfileStatusRow status={view.status} />
      <ProfileActionsRow actions={actions} moreRef={moreRef} />
    </View>
  );
}

/** Shimmer placeholder shaped like the popover header — shown while the peer
 *  profile is being fetched so the card appears instantly. `avatarSizeClassName`
 *  matches the surface (80px popover / 120px modal). */
export function ProfileHeaderSkeleton({
  avatarSizeClassName,
}: {
  avatarSizeClassName: string;
}): JSX.Element {
  return (
    <View className="gap-3">
      <View className="flex-row items-start justify-between">
        <Skeleton className={avatarSizeClassName} />
        <Skeleton className="w-[100px] h-[14px] rounded" />
      </View>
      <View className="gap-2">
        <Skeleton className="w-[140px] h-[18px] rounded" />
        <Skeleton className="w-[100px] h-[14px] rounded" />
      </View>
      <Skeleton className="w-[120px] h-[16px] rounded" />
      <View className="flex-row items-center gap-2">
        <Skeleton className="w-[176px] h-7 rounded-md" />
        <Skeleton className="w-7 h-7 rounded-md" />
        <Skeleton className="w-7 h-7 rounded-md" />
      </View>
    </View>
  );
}

export { ICON_MUTED, ChevronRight };
